import React, { useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSharedState } from '../hooks/SharedStateContext';

export default function AGI() {
  const { sharedState } = useSharedState();
  const { t } = useTranslation();
  // Double-buffer: two video elements for crossfade
  const videoRefA = useRef(null);
  const videoRefB = useRef(null);
  const activeBuffer = useRef('A'); // which buffer is currently visible
  // Track preloaded video elements by src for instant switching
  const preloadedCache = useRef({});

  const videos = useMemo(() => ({
    waiting: [
      { src: "/talking/video-light/waiting1.mp4", duration: 3 },
      { src: "/talking/video-light/waiting2.mp4", duration: 7 },
      { src: "/talking/video-light/waiting3.mp4", duration: 5 },
    ],
    talking: [
      { src: "/talking/video-light/talking1.mp4", duration: 8 },
      { src: "/talking/video-light/talking2.mp4", duration: 8 },
      { src: "/talking/video-light/talking3.mp4", duration: 7 },
      { src: "/talking/video-light/talking4.mp4", duration: 5 },
      { src: "/talking/video-light/talking5.mp4", duration: 8 },
    ]
  }), []);

  const preloadVideos = useCallback((videoList) => {
    videoList.forEach(video => {
      const src = video.src;
      if (preloadedCache.current[src]) return;
      const videoElement = document.createElement('video');
      videoElement.src = src;
      videoElement.preload = 'auto';
      videoElement.muted = true;
      videoElement.style.display = 'none';
      document.body.appendChild(videoElement);
      videoElement.load();
      preloadedCache.current[src] = true;
    });
  }, []);

  // Preload MP4s
  useEffect(() => {
    preloadVideos(videos.waiting);
    preloadVideos(videos.talking);
  }, [videos, preloadVideos]);

  // Crossfade switch: load new video on the hidden buffer, fade it in once ready
  const crossfadeToVideo = useCallback((src) => {
    const isA = activeBuffer.current === 'A';
    const incoming = isA ? videoRefB.current : videoRefA.current;
    const outgoing = isA ? videoRefA.current : videoRefB.current;

    if (!incoming || !outgoing) return;

    // Prepare the incoming (hidden) buffer
    incoming.src = src;
    incoming.load();

    const onReady = () => {
      incoming.removeEventListener('canplaythrough', onReady);
      incoming.play().catch(() => {});
      // Fade in incoming, fade out outgoing
      incoming.style.opacity = '1';
      outgoing.style.opacity = '0';
      activeBuffer.current = isA ? 'B' : 'A';
    };

    // If already cached/ready, fire immediately
    if (incoming.readyState >= 4) {
      onReady();
    } else {
      incoming.addEventListener('canplaythrough', onReady, { once: true });
      // Fallback: if canplaythrough doesn't fire within 300ms, show anyway
      setTimeout(() => {
        incoming.removeEventListener('canplaythrough', onReady);
        incoming.play().catch(() => {});
        incoming.style.opacity = '1';
        outgoing.style.opacity = '0';
        activeBuffer.current = isA ? 'B' : 'A';
      }, 300);
    }
  }, []);

  // Update source when talking state changes
  useEffect(() => {
    const mode = sharedState.talking ? 'talking' : 'waiting';
    const videoList = videos[mode];
    const random = videoList[Math.floor(Math.random() * videoList.length)];
    crossfadeToVideo(random.src);
  }, [sharedState.talking, videos, crossfadeToVideo]);

  // Ref callback to force muted attribute immediately on mount.
  const setVideoRefA = useCallback((el) => {
    videoRefA.current = el;
    if (el) {
      el.muted = true;
      el.defaultMuted = true;
    }
  }, []);

  const setVideoRefB = useCallback((el) => {
    videoRefB.current = el;
    if (el) {
      el.muted = true;
      el.defaultMuted = true;
    }
  }, []);

  return (
    <div className="agi-container">
      <video
        className="agi-video agi-video-buffer"
        ref={setVideoRefA}
        autoPlay
        muted
        playsInline
        webkit-playsinline=""
        loop
        style={{ opacity: 1 }}
      />
      <video
        className="agi-video agi-video-buffer"
        ref={setVideoRefB}
        muted
        playsInline
        webkit-playsinline=""
        loop
        style={{ opacity: 0 }}
      />

      {sharedState.conversationStatus === 'connected' && (
        <div className="agi-status-indicator">
          <p className="agi-status-text">
            {t('status_prefix')} {sharedState.conversationIsSpeaking ? t('status_speaking') : t('status_listening')}
          </p>
        </div>
      )}
    </div>
  );
}
